// Unified calendar service for loading and managing events from all sources
import { createClient } from '@blinkdotnew/sdk';
import { UnifiedCalendarEvent, UnifiedCalendar } from '@/types/unifiedCalendar';
import { convertDbEventToUnified } from './calendarConverter';
import { filterEvents, sortEventsByStartTime } from './calendarSearch';
import type { CalendarFilters } from '@/types/unifiedCalendar';

const blink = createClient({
  projectId: 'lemon-ui-agentic-email-ai-agent-ben3ou87',
  authRequired: false
});

export class UnifiedCalendarService {
  /**
   * Load all calendar events for a user from database
   */
  async loadUserEvents(userId: string): Promise<UnifiedCalendarEvent[]> {
    try {
      // Load from calendar_events_enhanced table
      const dbEvents = await blink.db.calendarEventsEnhanced.list({
        where: { userId },
        orderBy: { startTime: 'asc' },
        limit: 2000
      });

      // Convert to unified format
      const events = dbEvents.map((e: any) => convertDbEventToUnified(e));
      
      return sortEventsByStartTime(events);
    } catch (error) {
      console.error('Error loading calendar events:', error);
      throw error;
    }
  }

  /**
   * Load events for a specific date range
   */
  async loadEventsInRange(
    userId: string,
    startDate: Date,
    endDate: Date
  ): Promise<UnifiedCalendarEvent[]> {
    try {
      const allEvents = await this.loadUserEvents(userId);
      
      // Filter by date range
      return allEvents.filter(event => {
        const eventStart = new Date(event.start);
        const eventEnd = new Date(event.end);
        return eventStart < endDate && eventEnd > startDate;
      });
    } catch (error) {
      console.error('Error loading events in range:', error);
      throw error;
    }
  }

  /**
   * Search and filter events
   */
  async searchEvents(
    userId: string,
    filters: CalendarFilters
  ): Promise<UnifiedCalendarEvent[]> {
    try {
      // Load all events
      const allEvents = await this.loadUserEvents(userId);
      
      // Apply filters
      return filterEvents(allEvents, filters);
    } catch (error) {
      console.error('Error searching events:', error);
      throw error;
    }
  }

  /**
   * Get a single event by ID
   */
  async getEvent(userId: string, eventId: string): Promise<UnifiedCalendarEvent | null> {
    try {
      const events = await blink.db.calendarEventsEnhanced.list({
        where: { id: eventId, userId }
      });

      if (events.length === 0) {
        return null;
      }

      return convertDbEventToUnified(events[0]);
    } catch (error) {
      console.error('Error getting event:', error);
      return null;
    }
  }

  /**
   * Get available calendars for a user
   */
  async getUserCalendars(userId: string): Promise<UnifiedCalendar[]> {
    try {
      // Get unique calendar IDs from events
      const events = await blink.db.calendarEventsEnhanced.list({
        where: { userId }
      });

      const calendarMap = new Map<string, UnifiedCalendar>();

      events.forEach((event: any) => {
        const calendarId = event.calendarId || event.calendar_id || 'default';
        const provider = event.source || 'manual';
        
        if (!calendarMap.has(calendarId)) {
          calendarMap.set(calendarId, {
            id: calendarId,
            name: this.getCalendarName(calendarId, provider),
            provider: provider as any,
            primary: calendarId === 'primary' || calendarId === 'default'
          });
        }
      });

      return Array.from(calendarMap.values());
    } catch (error) {
      console.error('Error getting user calendars:', error);
      return [];
    }
  }

  /**
   * Trigger calendar sync from external providers
   */
  async syncCalendarEvents(userId: string): Promise<number> {
    try {
      // Check if user is authenticated
      if (!blink.auth.isAuthenticated()) {
        console.log('User not authenticated - skipping calendar sync');
        return 0;
      }

      // Get JWT token for authentication
      const token = localStorage.getItem('access-token');
      
      if (!token) {
        console.log('No access token available - skipping calendar sync');
        return 0;
      }

      // Use the getCalendarEventsByWeek function
      const response = await fetch('https://ben3ou87--getCalendarEventsByWeek.functions.blink.new', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const totalSynced = data.totalEvents || 0;
        
        if (totalSynced > 0) {
          console.log(`✅ Synced ${totalSynced} calendar events`);
        } else {
          console.log('No calendar events to sync');
        }
        
        return totalSynced;
      } else {
        const error = await response.json();
        console.log('Calendar sync skipped:', error.message || 'No accounts connected');
        return 0;
      }
    } catch (error) {
      console.log('Calendar sync unavailable:', error instanceof Error ? error.message : 'Unknown error');
      return 0;
    }
  }

  /**
   * Get events by date range (compatibility method for tests)
   */
  async getEventsByDateRange(start: Date, end: Date, query?: string): Promise<UnifiedCalendarEvent[]> {
    try {
      // Get current user
      const user = await blink.auth.me();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Load events in range
      const events = await this.loadEventsInRange(user.id, start, end);
      
      // Filter by query if provided
      if (query && query.trim()) {
        const queryLower = query.toLowerCase();
        return events.filter(event => 
          event.title?.toLowerCase().includes(queryLower) ||
          event.description?.toLowerCase().includes(queryLower) ||
          event.location?.toLowerCase().includes(queryLower)
        );
      }

      return events;
    } catch (error) {
      console.error('Error getting events by date range:', error);
      return [];
    }
  }

  /**
   * Create a calendar event (compatibility method for tests)
   */
  async createEvent(params: {
    title: string;
    description?: string;
    start: Date;
    end: Date;
    attendees?: string[];
    location?: string;
    isOnlineMeeting?: boolean;
    reminders?: Array<{ method: string; minutesBefore: number }>;
  }): Promise<{ id: string; success: boolean }> {
    try {
      const user = await blink.auth.me();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const eventId = `event_${Date.now()}`;
      
      await blink.db.calendarEventsEnhanced.create({
        id: eventId,
        userId: user.id,
        accountId: 'local',
        source: 'manual',
        providerEventId: eventId,
        title: params.title,
        description: params.description || '',
        location: params.location || '',
        startTime: params.start.toISOString(),
        endTime: params.end.toISOString(),
        startDate: params.start.toISOString().split('T')[0],
        endDate: params.end.toISOString().split('T')[0],
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        isAllDay: '0',
        isRecurring: '0',
        attendees: params.attendees ? JSON.stringify(params.attendees) : undefined,
        attendeeCount: params.attendees?.length || 0,
        status: 'confirmed',
        visibility: 'public',
        reminders: params.reminders ? JSON.stringify(params.reminders) : undefined,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });

      return { id: eventId, success: true };
    } catch (error) {
      console.error('Error creating calendar event:', error);
      throw error;
    }
  }

  /**
   * Update a calendar event (compatibility method for tests)
   */
  async updateEvent(eventId: string, updates: Partial<any>): Promise<void> {
    const updateData: any = {
      updatedAt: new Date().toISOString()
    };

    if (updates.title) updateData.title = updates.title;
    if (updates.description) updateData.description = updates.description;
    if (updates.start) {
      updateData.startTime = updates.start.toISOString();
      updateData.startDate = updates.start.toISOString().split('T')[0];
    }
    if (updates.end) {
      updateData.endTime = updates.end.toISOString();
      updateData.endDate = updates.end.toISOString().split('T')[0];
    }
    if (updates.location) updateData.location = updates.location;
    if (updates.attendees) {
      updateData.attendees = JSON.stringify(updates.attendees);
      updateData.attendeeCount = updates.attendees.length;
    }

    await blink.db.calendarEventsEnhanced.update(eventId, updateData);
  }

  /**
   * Delete a calendar event (compatibility method for tests)
   */
  async deleteEvent(eventId: string): Promise<void> {
    await blink.db.calendarEventsEnhanced.delete(eventId);
  }

  /**
   * Helper to get calendar name
   */
  private getCalendarName(calendarId: string, provider: string): string {
    if (calendarId === 'primary' || calendarId === 'default') {
      return 'My Calendar';
    }
    if (provider === 'google') {
      return `Google Calendar (${calendarId})`;
    }
    if (provider === 'outlook') {
      return `Outlook Calendar (${calendarId})`;
    }
    return calendarId;
  }
}

export const unifiedCalendarService = new UnifiedCalendarService();
